<script lang="ts">
	import { routeToDeviceSettings } from 'src/stores/ui/route-to-device-settings';
	import type { CloudRecording } from '../types/cloud-recordings';
	import { onDestroy, onMount } from 'svelte';
	import { getCloudRecordings } from 'src/services/cam-cloud/recordings';
	import { ButtonV2, Container, Text, Loader, Stack } from '@Shelly/shelly-design-system/index';
	import { format } from 'svelte-i18n';
	import { capFirstChar } from 'src/helpers/utilities';
	import RecordingsListItem from './RecordingsTab/RecordingsListItem.svelte';
	import NoResults from './RecordingsTab/NoResults.svelte';
	import { camera_previews } from 'src/components/Camera/stores/camera_previews';

	export let deviceId: string;

	const RECORDINGS_LIMIT = 3;

	let recordings: CloudRecording[] = [];
	let isLoading = true;
	let loadError = false;

	const loadRecordings = async () => {
		loadError = false;
		try {
			const result = await getCloudRecordings(deviceId, { limit: RECORDINGS_LIMIT, offset: 0 });
			recordings = result.items as CloudRecording[];
		} catch (error) {
			console.log('Failed to load recordings', error);
			loadError = true;
		} finally {
			isLoading = false;
		}
	};

	const handleRetry = () => {
		loadError = false;
		loadRecordings();
	};

	const navigateToSetting = (setting, subSetting, params) => {
		routeToDeviceSettings.setTarget(deviceId, setting, subSetting, params);
	};

	const handleSelect = (event) => {
		const recording = event.detail;
		navigateToSetting('camera_recordings', 'view', recording);
	};

	onMount(loadRecordings);

	const unsubscribe = camera_previews.onDeviceRecordingChange(deviceId, () => loadRecordings());
	onDestroy(unsubscribe);
</script>

<Text styleType="body" class="text-bold mt-m"
	>{$format('last_recordings_title', { default: 'Last Recordings' })}</Text
>
{#if isLoading}
	<div class="d-flex justify-content-center width-full p-m">
		<Loader />
	</div>
{:else if recordings.length > 0}
	<Stack gap="s">
		{#each recordings as recording}
			<RecordingsListItem {recording} selected={false} variant="simple" on:select={handleSelect} />
		{/each}
	</Stack>
{:else}
	<Container class="pb-l">
		<NoResults
			noResultsFound={loadError
				? {
						icon: 'fa-light fa-triangle-exclamation',
						title: $format('recordings_list_load_error', {
							default: "Couldn't load recordings",
						}),
						description: $format('recordings_list_load_error_description', {
							default: 'Something went wrong. Please try again.',
						}),
					}
				: undefined}
		/>
		{#if loadError}
			<div class="d-flex justify-content-center mt-m">
				<ButtonV2 variant="secondary" size="m" on:click={handleRetry}>
					{capFirstChar($format('button_try_again', { default: 'Try again' }))}
				</ButtonV2>
			</div>
		{/if}
	</Container>
{/if}
